/**
 * useDoorContext — resolves the active KCIS Ecosystem Door from window.location.hostname.
 *
 * Locked-down per the approved Ecosystem Architecture Lockdown document.
 * Every customer-facing page can read `doorKey` and theme accordingly without
 * touching shared logic.
 */
import { useMemo } from "react";

export const DOORS = {
  PORTAL:       "portal",       // KissedByCVon.com — ecosystem gateway
  CLINIC:       "clinic",       // KissedByCVon.com/clinic
  COACHING:     "coaching",     // ChristyVonta.com
  UNIVERSITY:   "university",   // KissedUniversity.com
  COMFORT_CARE: "comfort_care", // KissedByCVon.com/comfort-care
  ADMIN:        "admin",        // admin.kissedbycvon.com
  IDENTITY:     "identity",     // identity.christyvonta.com (legacy vanity host)
  CONNECT:      "connect",      // connect.christyvonta.com — Check In with Dr. C companion app
};

const HOSTNAME_DOORS = [
  { match: /(^|\.)admin\.kissedbycvon\.com$/i,        key: DOORS.ADMIN },
  { match: /(^|\.)connect\.christyvonta\.com$/i,      key: DOORS.CONNECT },
  { match: /(^|\.)identity\.christyvonta\.com$/i,     key: DOORS.IDENTITY },
  { match: /(^|\.)christyvonta\.com$/i,               key: DOORS.COACHING },
  { match: /(^|\.)kisseduniversity\.com$/i,           key: DOORS.UNIVERSITY },
  { match: /(^|\.)kissedbycvon\.com$/i,               key: DOORS.PORTAL },
];

// Path-prefix mapping inside KissedByCVon.com (Door 3/4 inhabit the same TLD)
const PATH_DOORS = [
  { match: /^\/clinic(\/|$)/i,        key: DOORS.CLINIC },
  { match: /^\/comfort-care(\/|$)/i,  key: DOORS.COMFORT_CARE },
];

// Per-door theme tokens (consumed by ThemeProvider / page chrome).
const THEMES = {
  [DOORS.PORTAL]: {
    label: "KISSED Ecosystem", accent: "#5C1E8A", surfaceDark: "#150B27",
    serif: "'Cormorant Garamond', Georgia, serif",
  },
  [DOORS.CLINIC]: {
    label: "KCIS Clinic Door™", accent: "#5C1E8A", surfaceDark: "#150B27",
    serif: "'Cormorant Garamond', Georgia, serif",
  },
  [DOORS.COACHING]: {
    label: "KCIS Coaching Door™", accent: "#5C1E8A", surfaceDark: "#1B0F2E",
    serif: "'Cormorant Garamond', Georgia, serif",
  },
  [DOORS.UNIVERSITY]: {
    label: "KCIS University Door™", accent: "#3D2266", surfaceDark: "#0F0820",
    serif: "'Cormorant Garamond', Georgia, serif",
  },
  [DOORS.COMFORT_CARE]: {
    label: "KCIS Comfort Care Door™", accent: "#2D6B50", surfaceDark: "#102218",
    serif: "'Cormorant Garamond', Georgia, serif",
  },
  [DOORS.ADMIN]:    { label: "KCIS Operating System", accent: "#5C1E8A", surfaceDark: "#0A0A14", serif: "'Cormorant Garamond', Georgia, serif" },
  [DOORS.IDENTITY]: { label: "Identity Assessment",   accent: "#5C1E8A", surfaceDark: "#1B0F2E", serif: "'Cormorant Garamond', Georgia, serif" },
  [DOORS.CONNECT]:  { label: "Check In with Dr. C",   accent: "#5C1E8A", surfaceDark: "#0B0715", serif: "'Cormorant Garamond', Georgia, serif" },
};


export function resolveDoor(hostname = "", pathname = "") {
  // Hostname wins. Then path-prefix for KissedByCVon.com sub-doors.
  for (const r of HOSTNAME_DOORS) {
    if (r.match.test(hostname)) {
      if (r.key === DOORS.PORTAL) {
        for (const p of PATH_DOORS) {
          if (p.match.test(pathname)) return p.key;
        }
        return DOORS.PORTAL;
      }
      return r.key;
    }
  }
  // Local/preview env — fall back to path-prefix discovery so the SPA
  // still themes correctly during development on the preview host.
  for (const p of PATH_DOORS) {
    if (p.match.test(pathname)) return p.key;
  }
  if (/^\/coaching(\/|$)/i.test(pathname))   return DOORS.COACHING;
  if (/^\/university(\/|$)/i.test(pathname)) return DOORS.UNIVERSITY;
  if (/^\/admin(\/|$)/i.test(pathname))      return DOORS.ADMIN;
  if (/^\/checkin(\/|$)/i.test(pathname))    return DOORS.CONNECT;
  return DOORS.PORTAL;
}


export function useDoorContext() {
  return useMemo(() => {
    if (typeof window === "undefined") {
      return { doorKey: DOORS.PORTAL, theme: THEMES[DOORS.PORTAL], hostname: "", pathname: "" };
    }
    const hostname = window.location.hostname || "";
    const pathname = window.location.pathname || "/";
    const doorKey = resolveDoor(hostname, pathname);
    return { doorKey, theme: THEMES[doorKey], hostname, pathname };
  }, []);
}
